import {
  getWebStorageItem,
  isWebStorageAvailable,
  setWebStorageItem
} from "src/utils/web-storage";

const ItemName = "keep-booking-counter" as const;
const MaxDeleteAttemptCount = 3;

type KeepBookingCounter = {
  cruiseCode: string;
  deleteAttemptCount: number;
};

type AllKeepBookingCounters = Record<string, KeepBookingCounter | undefined>;

const isKeepBookingCounter = (value: unknown): value is KeepBookingCounter =>
  typeof value === "object" &&
  value !== null &&
  "cruiseCode" in value &&
  typeof value.cruiseCode === "string" &&
  "deleteAttemptCount" in value &&
  typeof value.deleteAttemptCount === "number";

const getAllCounters = (): AllKeepBookingCounters => {
  const encodedCounters = getWebStorageItem("local", ItemName);
  if (!encodedCounters) return {};

  try {
    const parsedCounters: unknown = JSON.parse(encodedCounters);
    if (
      typeof parsedCounters !== "object" ||
      parsedCounters === null ||
      Array.isArray(parsedCounters)
    ) {
      return {};
    }

    const counters: AllKeepBookingCounters = {};
    for (const [key, value] of Object.entries(parsedCounters)) {
      if (isKeepBookingCounter(value)) counters[key] = value;
    }
    return counters;
  } catch {
    return {};
  }
};

const setAllCounters = (counters: AllKeepBookingCounters): void => {
  setWebStorageItem("local", ItemName, JSON.stringify(counters));
};

export const checkAndUpdateCounter = (cruiseCode: string): boolean => {
  if (!isWebStorageAvailable("local")) return true;

  const allCounters = getAllCounters();
  const existing = allCounters[cruiseCode];

  const newOrFound = existing
    ? { ...existing }
    : { cruiseCode, deleteAttemptCount: 0 };

  newOrFound.deleteAttemptCount++;
  const updatedCounter = { [cruiseCode]: newOrFound };
  // NOTE: to support multiple keep booking it would be
  //const updatedCounter = { ...allCounters, ...{ [cruiseCode]: newOrFound } };
  setAllCounters(updatedCounter);

  return newOrFound.deleteAttemptCount >= MaxDeleteAttemptCount;
};

export const resetCounter = (cruiseCode: string): void => {
  if (!isWebStorageAvailable("local")) return;

  const allCounters = getAllCounters();
  delete allCounters[cruiseCode];
  setAllCounters(allCounters);
};
